<template>
  <div class="guide-roles-permissions">
    <h2>Roles and permissions</h2>
    <p>
      Roles control what portal users can see and do within your organization's eSign
      workspace. Every team member is assigned one or more roles that determine their
      level of access, from full administrative control down to read-only visibility.
      Choosing the right roles ensures that each person has exactly the access they
      need to do their job without exposing sensitive settings or workflows.
    </p>

    <q-banner class="bg-blue-1 text-blue-9 q-mb-lg" rounded>
      <template v-slot:avatar>
        <q-icon name="info" color="blue-9" />
      </template>
      This page covers the portal's role-based access control (RBAC) for human users
      logged into the web interface. For the permission model used by API credentials
      (PATs and OAuth clients), see
      <a href="/Docs/IntegrationGuides?topic=authentication">Authentication &amp; Scopes</a>
      instead. API credentials use OAuth 2.0 scopes and operate independently of
      the roles below.
    </q-banner>

    <q-separator class="q-my-lg" />

    <h2>Role descriptions</h2>
    <p>
      The platform provides six built-in roles. Each role targets a specific persona
      and grants a distinct set of capabilities.
    </p>

    <h3>Tenant Admin</h3>
    <p>
      Full administrative control over the organization's eSign account. Manages team
      membership, billing, branding, API credentials, webhooks, and SSO configuration.
      This role has unrestricted access to every feature in the platform.
    </p>
    <q-banner class="bg-blue-1 text-blue-9 q-my-md" rounded>
      <strong>Typical persona:</strong> IT administrator or business owner.
    </q-banner>

    <h3>Billing Admin</h3>
    <p>
      Manages the subscription plan, payment methods, and invoices. Cannot access
      packages, templates, or signing workflows. This role gives finance staff access
      to billing without exposing confidential document content.
    </p>
    <q-banner class="bg-blue-1 text-blue-9 q-my-md" rounded>
      <strong>Typical persona:</strong> Finance department or accountant.
    </q-banner>

    <h3>Manager</h3>
    <p>
      Full access to all packages and templates across the organization. Can create,
      edit, and delete templates, and monitors all signing activity organization-wide.
      Managers are the operational backbone of a signing team.
    </p>
    <q-banner class="bg-blue-1 text-blue-9 q-my-md" rounded>
      <strong>Typical persona:</strong> Operations lead or office manager.
    </q-banner>

    <h3>Supervisor</h3>
    <p>
      Oversees all packages in the organization. Can view and manage packages (send,
      void, resend) but cannot create or modify templates. Supervisors have a broad
      view of signing activity without the ability to change the underlying template
      infrastructure.
    </p>
    <q-banner class="bg-blue-1 text-blue-9 q-my-md" rounded>
      <strong>Typical persona:</strong> Team lead.
    </q-banner>

    <h3>Sender</h3>
    <p>
      Creates and manages their own packages and templates. Can see shared templates
      but cannot modify other users' content. This is the most common role for
      individual contributors who need to send documents for signature.
    </p>
    <q-banner class="bg-blue-1 text-blue-9 q-my-md" rounded>
      <strong>Typical persona:</strong> Individual contributor, such as an agent, attorney, or recruiter.
    </q-banner>

    <h3>Viewer</h3>
    <p>
      Read-only access to all packages and templates. Cannot create, send, edit, or
      delete anything. This role suits staff who need to check signing status or
      download completed documents without the ability to initiate or modify
      workflows.
    </p>
    <q-banner class="bg-blue-1 text-blue-9 q-my-md" rounded>
      <strong>Typical persona:</strong> Receptionist or coordinator who checks signing status.
    </q-banner>

    <q-separator class="q-my-lg" />

    <h2>Permission matrix</h2>
    <p>
      The table below shows which capabilities each role grants. A checkmark means the
      role has that capability. A blank cell means it does not.
    </p>

    <div class="q-my-md" style="overflow-x: auto;">
      <table class="q-table" style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Capability</th>
            <th style="text-align: center; padding: 8px; border-bottom: 2px solid #e0e0e0;">Tenant Admin</th>
            <th style="text-align: center; padding: 8px; border-bottom: 2px solid #e0e0e0;">Billing Admin</th>
            <th style="text-align: center; padding: 8px; border-bottom: 2px solid #e0e0e0;">Manager</th>
            <th style="text-align: center; padding: 8px; border-bottom: 2px solid #e0e0e0;">Supervisor</th>
            <th style="text-align: center; padding: 8px; border-bottom: 2px solid #e0e0e0;">Sender</th>
            <th style="text-align: center; padding: 8px; border-bottom: 2px solid #e0e0e0;">Viewer</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">List all packages</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">List own packages only</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Create, edit, send, void packages</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Download signed documents</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">View audit trail</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">List all templates</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">List shared and own templates</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Create templates</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Edit or delete templates</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Use templates (create package from template)</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Manage signing groups</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">View signing groups</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Manage billing and subscription</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Manage team and users</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Manage API credentials, webhooks, branding, SSO</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">&#10003;</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;"></td>
          </tr>
        </tbody>
      </table>
    </div>

    <q-separator class="q-my-lg" />

    <h2>Multi-role assignment</h2>
    <p>
      Users can be assigned multiple roles simultaneously. When a user holds more than
      one role, their effective permissions are the union of all assigned roles.
      Permissions are additive, never subtractive. There is no deny rule. Adding a role
      can only grant additional capabilities.
    </p>
    <p>
      For example, a user assigned both Sender and Billing Admin can manage their own
      packages and also access billing settings. They still cannot see other users'
      packages (that requires Manager or Supervisor). They still cannot manage the team
      (that requires Tenant Admin).
    </p>

    <q-separator class="q-my-lg" />

    <h2>Data visibility</h2>
    <p>
      Roles affect not only what actions a user can perform, but also what data they can
      see. Understanding data visibility matters for organizations that handle sensitive
      or confidential documents.
    </p>
    <ul>
      <li>
        <strong>Tenant Admin, Manager, and Supervisor</strong> see all packages and
        templates across the entire organization, regardless of who created them.
      </li>
      <li>
        <strong>Sender</strong> sees only the packages they created. For templates,
        they see shared templates plus any templates they created themselves.
      </li>
      <li>
        <strong>Viewer</strong> sees all packages and templates across the organization,
        but in a strictly read-only capacity.
      </li>
      <li>
        <strong>Billing Admin</strong> sees only billing and subscription data. They
        have no visibility into packages, templates, or signing activity.
      </li>
    </ul>

    <q-banner class="bg-amber-1 text-amber-10 q-my-md" rounded>
      <strong>Important:</strong> The Billing Admin role is intentionally isolated from
      document content. You can grant invoice and payment access to finance staff
      without exposing the contents of confidential agreements, HR documents, or legal
      contracts.
    </q-banner>

    <q-separator class="q-my-lg" />

    <h2>Best practices</h2>
    <ul>
      <li>
        <strong>Use the principle of least privilege.</strong> Assign the minimum roles
        each person needs to do their job. Broader access can always be granted later.
      </li>
      <li>
        <strong>Assign Sender to most individual contributors.</strong> Agents,
        recruiters, attorneys, and account managers typically need to create and
        manage their own packages but do not need organization-wide visibility.
      </li>
      <li>
        <strong>Use Manager for team leads who need template management.</strong> If
        someone needs to maintain your organization's library of templates and oversee
        all signing activity, Manager is the right fit.
      </li>
      <li>
        <strong>Keep Tenant Admin limited.</strong> One or two people is typically
        enough. The Tenant Admin role has unrestricted access to every setting,
        including API credentials and SSO configuration. Limit it to your IT
        administrator or business owner.
      </li>
      <li>
        <strong>Billing Admin is designed for finance.</strong> If your accountant or
        finance team needs to manage invoices and payment methods, Billing Admin gives
        them exactly that access without the ability to view or modify confidential
        documents.
      </li>
      <li>
        <strong>Combine roles when a single role is not enough.</strong> A team lead
        who sends their own packages and also needs template management can hold both
        Sender and Manager. An office admin who handles billing and monitors signing
        status can hold both Billing Admin and Viewer.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>Related</h2>
    <ul>
      <li><a href="/Docs/IntegrationGuides?topic=authentication">Authentication &amp; Scopes</a>. The OAuth scope model used by API credentials (distinct from portal RBAC).</li>
    </ul>

    <section class="dev-section">
      <h2>For developers and enterprise integration</h2>
      <p>
        Portal role assignments are not managed through the API. There is no endpoint
        for creating users, assigning roles, or listing team membership. All of that
        lives under <strong>Settings</strong> then <strong>Team</strong> in the portal
        and requires the <code>TenantAdmin</code> portal role.
      </p>
      <p>
        This keeps the API surface tight around integration concerns, such as sending
        packages, receiving webhooks, or managing signers for a given package, and
        leaves organizational concerns, such as who works here and what can they see,
        as a portal-admin responsibility. For API callers, the relevant permission
        model is the scope system covered in
        <a href="/Docs/IntegrationGuides?topic=authentication">Authentication &amp; Scopes</a>.
      </p>
      <h3>What that means in practice</h3>
      <ul>
        <li>
          <strong>Automating user provisioning</strong>, for example auto-adding a new
          hire to your eSign tenant, is not possible via API. Use the portal's Team
          page or SCIM provisioning if enabled on your plan.
        </li>
        <li>
          <strong>Reading the current user's role from a signed-in portal session</strong>
          is available via <code>window._USER_PERMISSIONS</code> on server-rendered
          portal pages. This is intended for the portal's own UI, not for external
          integrations.
        </li>
        <li>
          <strong>API credential authentication is tenant-scoped, not user-scoped.</strong>
          A PAT or OAuth client authenticates as the tenant, not as a specific user.
          The creator's identity is recorded for audit but does not affect what the
          credential can access. That is determined entirely by the credential's
          scope set.
        </li>
      </ul>
      <h3>MCP tools</h3>
      <p>
        No MCP tools are exposed for role or user management, for the same reason. This
        is a portal-admin concern, not an integration concern.
      </p>
    </section>
  </div>
</template>

<script>
export default {
  name: 'GuideRolesPermissions'
};
</script>
